import { Input, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, Directive } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { Deferred, IPromiseSource } from "../../helpers/deferred";
import { OperationCanceledException } from "../../exceptions/operation-canceled-exception";
import { TimeoutFunctionWrapper } from "../../helpers/timeout-function-wrapper";
import { BaseComponentContext } from "../../services/base-component-context.service";


@Directive()
export abstract class BaseComponent<TRequest, TViewModel> implements AfterViewInit, OnDestroy, OnChanges {
        private queryParamsListener: Subscription;
    private __requestModel: TRequest;
    private _componentCancellationTokenDeferred: Deferred;    
    private _refreshViewModelCancellationTokenDeferred: Deferred;
    private previousState: string;
    private isDisposed: boolean;


    public get requestModel(): TRequest {
            return this.__requestModel;
    }

    @Input()
    public set requestModel(value: TRequest) {
            const model = this.createRequestModel();
        Object.assign(model, value);

        this.__requestModel = model;
    }

    public viewModel: TViewModel;
    public isRootComponent: boolean = false;
    public isLoading: boolean = false;

    protected actionName: string;
    protected replaceHistoryOnRequestModelChanged: boolean;    

    protected readonly timeoutFunctionWrapper: TimeoutFunctionWrapper;

    protected get componentCancellationTokenSource(): IPromiseSource {
            return this._componentCancellationTokenDeferred;
    }


    protected get refreshViewModelCancellationToken(): Promise<any> {
            return this._refreshViewModelCancellationTokenDeferred.promise();
    }
    
    constructor(protected componentContext: BaseComponentContext, protected route: ActivatedRoute) {
            this.isRootComponent = this instanceof (route.component as any);
        this.replaceHistoryOnRequestModelChanged = false;
        this._componentCancellationTokenDeferred = new Deferred();
        this._refreshViewModelCancellationTokenDeferred = this._componentCancellationTokenDeferred.createDeferred();         
        this.timeoutFunctionWrapper = new TimeoutFunctionWrapper(this._componentCancellationTokenDeferred.promise());
    }

    protected readonly goBack = (data?: any) => {
            if(this.isDisposed){
                return;
        }
        
        this.componentContext.navService.navigateBack(data);
    };

    ngAfterViewInit() {
            setTimeout(() => {
                if (this.isRootComponent) {
                    this.queryParamsListener = this.route.queryParams.subscribe(
                    val => {
    
                        const model = this.createRequestModel();
                        if (val.params) {
                                const modelFromUrl = JSON.parse(val.params);

                            Object.assign(model, modelFromUrl);
                        }

                        this.previousState = this.convertRequestModelToJson();

                        this.__requestModel = model;

                        this.refreshViewModel();
                    }
                );
            }
        }, 0);
    }

    ngOnChanges(changes: SimpleChanges): void {
            if (changes.requestModel) {
                this.refresh();
        }
    }

  ngOnDestroy() {
    
    this.isDisposed = true;

    if (this.queryParamsListener) {
          this.queryParamsListener.unsubscribe();
    }

    if (this._componentCancellationTokenDeferred) {
          this._componentCancellationTokenDeferred.resolve();
    }
  }

    protected abstract createRequestModel(): TRequest;

    protected onViewModelChanged() {
    
    }


    protected refreshViewModel() {
    
        if(this.isDisposed){
                return;
        }

        this.resolveAndCreateRefreshViewModelCancellationTokenDeffered();
        const cancellationPromise = this._refreshViewModelCancellationTokenDeferred.promise();

        //TODO: add Task.delay
        
        this.isLoading = true;
        const request = this.convertRequestModelToJson();
        this.componentContext.http.getJsonAsync<TViewModel>(
            this.actionName + "?request=" + encodeURIComponent(request), null,
            cancellationPromise).then(val => {
                    this.viewModel = val;
                this.isLoading = false;
                this.onViewModelChanged();
            }, error => {
                    if (this.isRootComponent && !(error instanceof OperationCanceledException)) {
                        this.goBack();
                }
                this.isLoading = false;
            });
    }

    // Before rename check in ModalService call from 'as any' instance
    protected refresh() {
            if(this.isDisposed){
                return;
        }

        if (this.isRootComponent) {
                this.updateUrlByRequestModel();
        }
        else {
                this.refreshViewModel();
        }
    }

    private updateUrlByRequestModel() {
            const currentState = this.convertRequestModelToJson();

        (this.__requestModel as any)['____timestamp'] = this.createTimestamp()

        const queryParamsModel = JSON.parse(this.convertRequestModelToJson(true));

        if (!this.replaceHistoryOnRequestModelChanged && this.previousState != currentState) {
                this.componentContext.navService.updateQueryParams(queryParamsModel);
        } else {
                this.componentContext.navService.updateQueryParams(queryParamsModel, true);
        }
    }

    private convertRequestModelToJson(keepTimeStamp: boolean = false): string {
            if (keepTimeStamp) {
                return JSON.stringify(this.__requestModel, (key, value) => value != undefined && value !== '' ? value : undefined);
        }
        else {
                return JSON.stringify(this.__requestModel, (key, value) => key != '____timestamp' && value != undefined && value !== '' ? value : undefined);
        }
    }

    private createTimestamp(): string {
            const date = new Date();

        return date.getFullYear().toString() + date.getMonth().toString() + date.getDate().toString() + date.getHours().toString() + date.getMinutes().toString() + date.getSeconds().toString();
    }

    private resolveAndCreateRefreshViewModelCancellationTokenDeffered() {        
            if (this._refreshViewModelCancellationTokenDeferred) {
                this._refreshViewModelCancellationTokenDeferred.resolve();
        }

        this._refreshViewModelCancellationTokenDeferred = this._componentCancellationTokenDeferred.createDeferred();
    }
}